import { useState, useRef, useEffect, useCallback } from "react";
import axios from "axios";
import moment from "moment";

const verbose = true; // set to true to enable debug logging
const vlog = (...args) => verbose && console.log(...args);

// Waveform configuration
const WAVEFORM_BAR_COUNT = 14;
const DEFAULT_WAVEFORM = Array(WAVEFORM_BAR_COUNT).fill(20);

const mergeBuffers = (buffers) => {
  let length = 0;
  buffers.forEach((buffer) => {
    length += buffer.length;
  });

  const result = new Float32Array(length);
  let offset = 0;

  buffers.forEach((buffer) => {
    result.set(buffer, offset);
    offset += buffer.length;
  });

  return result;
};

const floatTo16BitPCM = (view, offset, input) => {
  for (let i = 0; i < input.length; i++, offset += 2) {
    const s = Math.max(-1, Math.min(1, input[i]));
    view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }
};

const writeString = (view, offset, string) => {
  for (let i = 0; i < string.length; i++) {
    view.setUint8(offset + i, string.charCodeAt(i));
  }
};

const encodeWav = (audioBuffer, sampleRate) => {
  const buffer = new ArrayBuffer(44 + audioBuffer.length * 2);
  const view = new DataView(buffer);

  writeString(view, 0, "RIFF");
  view.setUint32(4, 36 + audioBuffer.length * 2, true);
  writeString(view, 8, "WAVE");
  writeString(view, 12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  writeString(view, 36, "data");
  view.setUint32(40, audioBuffer.length * 2, true);
  floatTo16BitPCM(view, 44, audioBuffer);

  return new Blob([view], { type: "audio/wav" });
};

const createWavBlob = (buffers, sampleRate) => {
  const merged = mergeBuffers(buffers);
  return encodeWav(merged, sampleRate);
};

const getWaveformBars = (samples, barCount = WAVEFORM_BAR_COUNT) => {
  vlog("getWaveformBars called with samples:", samples?.length);
  
  if (!samples || !samples.length) {
    vlog("getWaveformBars: returning default waveform");
    return [...DEFAULT_WAVEFORM];
  }

  const chunkSize = Math.max(1, Math.floor(samples.length / barCount));
  vlog("getWaveformBars: chunkSize =", chunkSize);

  const result = Array.from({ length: barCount }, (_, barIndex) => {
    const start = barIndex * chunkSize;
    const end = Math.min(samples.length, start + chunkSize);

    let total = 0;
    for (let i = start; i < end; i += 1) {
      total += Math.abs(samples[i]);
    }

    const average = total / Math.max(1, end - start);
    const value = Math.round(Math.max(10, Math.min(100, average * 260)));
    vlog(`getWaveformBars: bar ${barIndex} = ${value}`);
    return value;
  });
  
  vlog("getWaveformBars: returning result =", result);
  return result;
};

const decodeAudioBlob = async (audioBlob) => {
  const AudioContextCtor = window.AudioContext || window.webkitAudioContext;
  const audioContext = new AudioContextCtor();
  const arrayBuffer = await audioBlob.arrayBuffer();
  const decoded = await audioContext.decodeAudioData(arrayBuffer);
  const samples = new Float32Array(decoded.getChannelData(0));
  await audioContext.close();

  return {
    samples,
    sampleRate: decoded.sampleRate,
    duration: decoded.duration,
  };
};

// Helper function to fetch recorded audio for a specific sentence
export const fetchRecordedAudio = async ({ userId, storyId, sentenceId, groupId, mode = "user" }) => {
  try {
    // Prepare query parameters
    const params = {
      storyId: storyId,
      sentenceId: sentenceId
    };
    
    // For single user mode, include userId
    if (mode === "user") {
      params.userId = userId;
    }
    
    // Always include groupId (0 for single user mode)
    params.groupId = mode === "group" && groupId ? groupId : 0;

    const response = await axios.get(`/api/audioRecords/filter`, { params });
    
    if (response.data && response.data.length > 0) {
      // For group mode, filter for group recordings with visibility 'group'
      if (mode === "group" && groupId) {
        const groupRecordings = response.data.filter(rec => rec.visibility === 'group');
        if (groupRecordings.length > 0) {
          return groupRecordings[0]; // Return the first group recording
        }
        return null;
      }
      
      // For user mode, return the first recording
      return response.data[0];
    }
    
    return null;
  } catch (error) {
    console.error("Error fetching recorded audio:", error);
    return null;
  }
};

export const useAudioPlayback = ({ audioUrl }) => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentWaveform, setCurrentWaveform] = useState(Array(WAVEFORM_BAR_COUNT).fill(20)); // Default waveform

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return undefined;

    const handleTimeUpdate = () => {
      if (!audio.duration) {
        setProgress(0);
        return;
      }

      const currentTimeProgress = (audio.currentTime / audio.duration) * 100 || 0;
      setProgress(currentTimeProgress);
      
      // Update waveform visualization during playback
      // This is a simplified approach - in a real app you'd analyze the actual audio data
      if (currentTimeProgress > 0) {
        // Simulate animated waveform by varying the bars
        const animatedWaveform = Array.from({ length: WAVEFORM_BAR_COUNT }, (_, i) => {
          // Create some variation based on progress and bar index
          const variation = Math.sin((currentTimeProgress / 100) * Math.PI * 2 + i) * 10;
          const baseValue = 20 + variation;
          return Math.max(10, Math.min(80, Math.round(baseValue)));
        });
        setCurrentWaveform(animatedWaveform);
      }
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setProgress(100);
      // Reset to static waveform when playback ends
      setCurrentWaveform(Array(WAVEFORM_BAR_COUNT).fill(20));
    };

    const handlePause = () => {
      setIsPlaying(false);
    };

    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.addEventListener("ended", handleEnded);
    audio.addEventListener("pause", handlePause);

    return () => {
      audio.removeEventListener("timeupdate", handleTimeUpdate);
      audio.removeEventListener("ended", handleEnded);
      audio.removeEventListener("pause", handlePause);
    };
  }, [audioUrl]);

  const togglePlay = useCallback(async () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (audio.paused) {
      await audio.play();
      setIsPlaying(true);
      return;
    }

    audio.pause();
    setIsPlaying(false);
  }, []);

  const handleSeek = useCallback((event) => {
    const audio = audioRef.current;
    if (!audio || !audio.duration) return;

    const rect = event.currentTarget.getBoundingClientRect();
    const clickX = event.clientX - rect.left;
    const width = rect.width || 1;
    const newTime = (clickX / width) * audio.duration;

    audio.currentTime = Math.max(0, Math.min(newTime, audio.duration));
  }, []);

  return {
    audioRef,
    isPlaying,
    progress,
    currentWaveform,
    togglePlay,
    handleSeek,
  };
};

export const useSpeechRecognition = ({ lang = "de-DE", onChange, value = "" }) => {
  const [listening, setListening] = useState(false);
  const [interimValue, setInterimValue] = useState("");
  const recognitionRef = useRef(null);
  const valueRef = useRef(value ?? "");

  useEffect(() => {
    valueRef.current = value ?? "";
  }, [value]);

  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const isIOS = /iPad|iPhone|iPod/.test(window.navigator.userAgent);

    if (!SpeechRecognition || isIOS) {
      return undefined;
    }

    const recog = new SpeechRecognition();
    recog.continuous = true;
    recog.interimResults = true;
    recog.lang = lang;

    recog.onresult = (event) => {
      let finalTranscript = "";
      let interimTranscript = "";

      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        if (event.results[i].isFinal) {
          finalTranscript += `${event.results[i][0].transcript} `;
        } else {
          interimTranscript += event.results[i][0].transcript;
        }
      }

      if (finalTranscript) {
        const newVal = `${valueRef.current} ${finalTranscript}`.trim();
        valueRef.current = newVal;
        onChange && onChange(newVal);
        setInterimValue("");
      } else {
        setInterimValue(interimTranscript);
      }
    };

    recog.onerror = () => {
      setListening(false);
    };

    recog.onend = () => setListening(false);
    recognitionRef.current = recog;

    return () => {
      try {
        recog.abort();
      } catch {
        // no-op
      }
    };
  }, [lang, onChange]);

  const startListening = useCallback(() => {
    if (!recognitionRef.current || listening) return;

    try {
      recognitionRef.current.start();
      setListening(true);
    } catch {
      setListening(false);
    }
  }, [listening]);

  const stopListening = useCallback(() => {
    if (!recognitionRef.current) return;

    recognitionRef.current.stop();
    setListening(false);
  }, []);

  return {
    listening,
    interimValue,
    startListening,
    stopListening,
  };
};

export const useRecorder = ({ storyId, sentenceId, userId, groupId, visibility }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [audioUrl, setAudioUrl] = useState("");
  const [tempRecordings, setTempRecordings] = useState([]);
  const [audioList, setAudioList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [waveformData, setWaveformData] = useState([...DEFAULT_WAVEFORM]);
  const [animatedWaveformData, setAnimatedWaveformData] = useState([...DEFAULT_WAVEFORM]);
  const [recordingDeleted, setRecordingDeleted] = useState(false);
  vlog("Initial waveformData state:", [...DEFAULT_WAVEFORM]);

  const audioContextRef = useRef(null);
  const processorRef = useRef(null);
  const sourceRef = useRef(null);
  const gainNodeRef = useRef(null);
  const streamRef = useRef(null);
  const recordedBuffersRef = useRef([]);
  const sampleRateRef = useRef(44100);
  const currentAudioRef = useRef(null);
  const waveformDataRef = useRef([...DEFAULT_WAVEFORM]);
  const waveformFrameRef = useRef(null);
  const playbackStartTimeRef = useRef(null);
  const playbackIntervalRef = useRef(null);
  const recordedWaveformRef = useRef(null);
  const playbackSamplesRef = useRef(null);
  const playbackSampleRateRef = useRef(44100);

  const fetchRecordings = useCallback(async () => {
    if (!userId || !storyId || !sentenceId) {
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(
        `/api/audioRecords/filter?userId=${userId}&storyId=${storyId}&sentenceId=${sentenceId}&groupId=${groupId}`
      );
      setAudioList(response.data || []);
      // Reset the recording deleted flag when we fetch new recordings
      setRecordingDeleted(false);
    } catch (error) {
      console.error("Error fetching audio records:", error);
    } finally {
      setLoading(false);
    }
  }, [userId, storyId, sentenceId, groupId, setRecordingDeleted]);

  useEffect(() => {
    fetchRecordings();
  }, [fetchRecordings]);

  const uploadAudioRecord = useCallback(async (audioBlob) => {
    const formData = new FormData();
    formData.append("file", audioBlob, ".wav");
    const response = await axios.post("/api/upload-audio-record", formData);
    return response.data;
  }, []);

  const saveRecording = useCallback(
    async (audioBlob) => {
      if (!audioBlob) {
        throw new Error("No audio blob available to save.");
      }

      const filePath = await uploadAudioRecord(audioBlob);
      const audioRecordData = {
        storyId,
        sentenceId,
        userId,
        groupId,
        visibility,
        date: moment(Date.now()).format("YYYY-MM-DD HH:mm:ss"),
        fileName: filePath,
      };

      await axios.post(`/api/audioRecords`, audioRecordData);
      setRecordingDeleted(false); // Reset the recording deleted flag when saving new recording
      await fetchRecordings();
      return filePath;
    },
    [storyId, sentenceId, userId, groupId, visibility, uploadAudioRecord, fetchRecordings]
  );

  const startRecording = useCallback(async () => {
    vlog("startRecording called");
    try {
      if (currentAudioRef.current) {
        currentAudioRef.current.pause();
        currentAudioRef.current = null;
      }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      vlog("Got media stream");
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      vlog("Created audio context");
      const source = audioContext.createMediaStreamSource(stream);
      vlog("Created media stream source");
      const processor = audioContext.createScriptProcessor(4096, 1, 1);
      vlog("Created script processor");
      const gainNode = audioContext.createGain();
      vlog("Created gain node");

      recordedBuffersRef.current = [];
      sampleRateRef.current = audioContext.sampleRate;
      waveformDataRef.current = [...DEFAULT_WAVEFORM];
      setWaveformData([...DEFAULT_WAVEFORM]);

      gainNode.gain.value = 0;
      vlog("Setting up processor.onaudioprocess");
      processor.onaudioprocess = (event) => {
        vlog("onaudioprocess: called");
        const channelData = event.inputBuffer.getChannelData(0);
        vlog("onaudioprocess: channelData length =", channelData.length);
        recordedBuffersRef.current.push(new Float32Array(channelData));
        
        // Update waveform data immediately for better responsiveness
        const newWaveform = getWaveformBars(channelData, WAVEFORM_BAR_COUNT);
        waveformDataRef.current = newWaveform;
        
        // Cancel any pending animation frame
        if (waveformFrameRef.current) {
          window.cancelAnimationFrame(waveformFrameRef.current);
        }
        
        // Schedule immediate update
        waveformFrameRef.current = window.requestAnimationFrame(() => {
          vlog("Animation frame: updating waveformData to", [...waveformDataRef.current]);
          setWaveformData((prev) => {
            vlog("setWaveformData called with", [...waveformDataRef.current], "prev was", prev);
            return [...waveformDataRef.current];
          });
          waveformFrameRef.current = null;
        });
      };
      vlog("Processor setup complete");

      vlog("Connecting audio nodes");
      source.connect(processor);
      vlog("Source connected to processor");
      processor.connect(gainNode);
      vlog("Processor connected to gainNode");
      gainNode.connect(audioContext.destination);
      vlog("GainNode connected to destination");

      streamRef.current = stream;
      sourceRef.current = source;
      audioContextRef.current = audioContext;
      processorRef.current = processor;
      gainNodeRef.current = gainNode;
      vlog("All refs set, setting isRecording to true");
      setIsRecording(true);
    } catch (error) {
      console.error("Failed to start recording:", error);
      throw error;
    }
  }, []);

  const stopRecording = useCallback(() => {
    vlog("stopRecording: called");
    const processor = processorRef.current;
    const audioContext = audioContextRef.current;
    const stream = streamRef.current;

    if (!processor || !audioContext || !stream) {
      return null;
    }

    sourceRef.current?.disconnect();
    processor.disconnect();
    gainNodeRef.current?.disconnect();
    audioContext.close();
    stream.getTracks().forEach((track) => track.stop());

    const merged = mergeBuffers(recordedBuffersRef.current);
    vlog("stopRecording: merged buffer length =", merged.length);
    const wavBlob = createWavBlob(recordedBuffersRef.current, sampleRateRef.current);
    const url = URL.createObjectURL(wavBlob);
    const waveform = getWaveformBars(merged, WAVEFORM_BAR_COUNT);
    vlog("stopRecording: final waveform =", waveform);
    vlog("stopRecording: voice data", {
      bufferCount: recordedBuffersRef.current.length,
      sampleCount: merged.length,
      sampleRate: sampleRateRef.current,
      durationSeconds: merged.length / sampleRateRef.current,
      blobSize: wavBlob.size,
      blobType: wavBlob.type,
      waveform,
    });

    setAudioUrl(url);
    setTempRecordings([{ audioBlob: wavBlob, audioUrl: url }]);
    setWaveformData(waveform);
    waveformDataRef.current = waveform;
    
    // Store the recorded waveform data separately so we can use it later
    recordedBuffersRef.current.waveform = waveform;
    recordedWaveformRef.current = waveform;
    playbackSamplesRef.current = merged;
    playbackSampleRateRef.current = sampleRateRef.current;
    
    // Cancel any pending animation frame
    if (waveformFrameRef.current) {
      window.cancelAnimationFrame(waveformFrameRef.current);
      waveformFrameRef.current = null;
    }
    
    vlog("stopRecording: setting waveformData to", waveform);
    setIsRecording(false);

    processorRef.current = null;
    sourceRef.current = null;
    gainNodeRef.current = null;
    audioContextRef.current = null;
    streamRef.current = null;

    return wavBlob;
  }, []);

  const resetRecording = useCallback(() => {
    if (processorRef.current || audioContextRef.current || streamRef.current) {
      try {
        sourceRef.current?.disconnect();
        processorRef.current?.disconnect();
        gainNodeRef.current?.disconnect();
        audioContextRef.current?.close();
        streamRef.current?.getTracks().forEach((track) => track.stop());
      } catch (error) {
        console.error("Error resetting recording:", error);
      }

      processorRef.current = null;
      sourceRef.current = null;
      gainNodeRef.current = null;
      audioContextRef.current = null;
      streamRef.current = null;
      setIsRecording(false);
    }

    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current = null;
    }

    if (waveformFrameRef.current) {
      window.cancelAnimationFrame(waveformFrameRef.current);
      waveformFrameRef.current = null;
    }
    
    // Clear playback interval if active
    if (playbackIntervalRef.current) {
      clearInterval(playbackIntervalRef.current);
      playbackIntervalRef.current = null;
    }

    waveformDataRef.current = [...DEFAULT_WAVEFORM];
    setWaveformData([...DEFAULT_WAVEFORM]);
    setAnimatedWaveformData([...DEFAULT_WAVEFORM]);
    setAudioUrl("");
    setTempRecordings([]);
    setIsPlaying(false);
  }, []);

  const pausePlayback = useCallback(() => {
    if (currentAudioRef.current && !currentAudioRef.current.paused) {
      currentAudioRef.current.pause();
      setIsPlaying(false);
    }
  }, []);

  const playLocalRecording = useCallback(
    (url) => {
      if (!url) return;

      if (currentAudioRef.current) {
        currentAudioRef.current.pause();
        currentAudioRef.current = null;
      }

      const audio = new Audio(url);
      currentAudioRef.current = audio;
      setIsPlaying(true);
      
      // Store the recorded waveform for animation
      if (recordedBuffersRef.current.waveform) {
        vlog("playLocalRecording: storing waveform data for animation", recordedBuffersRef.current.waveform);
        recordedWaveformRef.current = recordedBuffersRef.current.waveform;
        // Restore the waveform data for the recorded audio
        setWaveformData(recordedBuffersRef.current.waveform);
        waveformDataRef.current = recordedBuffersRef.current.waveform;
      }

      if (playbackIntervalRef.current) {
        clearInterval(playbackIntervalRef.current);
      }
      
      playbackIntervalRef.current = setInterval(() => {
        if (!currentAudioRef.current) return;
        
        const audio = currentAudioRef.current;
        if (audio.paused || audio.ended) return;
        
        const samples = playbackSamplesRef.current;
        const sampleRate = playbackSampleRateRef.current;
        const windowSize = Math.max(WAVEFORM_BAR_COUNT, Math.floor(sampleRate * 0.25));
        const start = Math.max(0, Math.floor(audio.currentTime * sampleRate));
        const end = Math.min(samples?.length || 0, start + windowSize);
        const animatedWaveform = samples?.length
          ? getWaveformBars(samples.slice(start, end), WAVEFORM_BAR_COUNT)
          : recordedWaveformRef.current || [...DEFAULT_WAVEFORM];
        
        setAnimatedWaveformData(animatedWaveform);
      }, 100); // Update every 100ms

      audio.play();
      audio.onpause = () => {
        setIsPlaying(false);
        // Stop animation
        if (playbackIntervalRef.current) {
          clearInterval(playbackIntervalRef.current);
          playbackIntervalRef.current = null;
        }
      };
      audio.onended = () => {
        setIsPlaying(false);
        currentAudioRef.current = null;
        
        // Stop animation
        if (playbackIntervalRef.current) {
          clearInterval(playbackIntervalRef.current);
          playbackIntervalRef.current = null;
        }
        
        // Restore default waveform when playback ends
        vlog("playLocalRecording: resetting to default waveform");
        setWaveformData([...DEFAULT_WAVEFORM]);
        setAnimatedWaveformData([...DEFAULT_WAVEFORM]);
        waveformDataRef.current = [...DEFAULT_WAVEFORM];
      };
    },
    []
  );

  const playExistingRecording = useCallback(
    async (filePath) => {
      if (!filePath) return;

      if (currentAudioRef.current) {
        currentAudioRef.current.pause();
        currentAudioRef.current = null;
      }

      const response = await axios.get(`/api/audioRecords/audiofile?fileName=${filePath}`, {
        responseType: "blob",
      });
      const audioBlob = response.data;
      const decodedVoiceData = await decodeAudioBlob(audioBlob);
      playbackSamplesRef.current = decodedVoiceData.samples;
      playbackSampleRateRef.current = decodedVoiceData.sampleRate;
      recordedWaveformRef.current = getWaveformBars(decodedVoiceData.samples, WAVEFORM_BAR_COUNT);
      vlog("playExistingRecording: voice data", {
        sampleCount: decodedVoiceData.samples.length,
        sampleRate: decodedVoiceData.sampleRate,
        durationSeconds: decodedVoiceData.duration,
        blobSize: audioBlob.size,
        blobType: audioBlob.type,
        waveform: recordedWaveformRef.current,
      });
      const url = URL.createObjectURL(audioBlob);
      playLocalRecording(url);
    },
    [playLocalRecording]
  );

  const deleteRecording = useCallback(async (recordingId) => {
    try {
      await axios.delete(`/api/audioRecords/${recordingId}`);
      setRecordingDeleted(true);
      await fetchRecordings(); // Refresh the recordings list
    } catch (error) {
      console.error("Error deleting recording:", error);
      throw error;
    }
  }, [fetchRecordings, setRecordingDeleted]);

  return {
    isRecording,
    audioUrl,
    tempRecordings,
    audioList,
    loading,
    isPlaying,
    waveformData,
    animatedWaveformData, // For animated playback visualization
    recordingDeleted,
    setRecordingDeleted,
    startRecording,
    stopRecording,
    resetRecording,
    pausePlayback,
    saveRecording,
    playLocalRecording,
    playExistingRecording,
    fetchRecordings,
    deleteRecording,
  };
};
